import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAutoTaggingPlanTypes,
  getSubTypesForObjectType as getSubTypesForObjectTypeFromList,
  resolveDefaultObjectType,
  type AutoTaggingPlanNamedOption,
  type AutoTaggingPlanTypeOption,
} from "../services/autoTaggingService";

export interface UseAutoTaggingPlanTypesResult {
  planTypes: AutoTaggingPlanTypeOption[];
  objectTypeOptions: AutoTaggingPlanNamedOption[];
  defaultObjectType: string;
  getSubTypesForObjectType: (objectType: string) => AutoTaggingPlanNamedOption[];
  loading: boolean;
  error: string | null;
}

export function useAutoTaggingPlanTypes(): UseAutoTaggingPlanTypesResult {
  const [planTypes, setPlanTypes] = useState<AutoTaggingPlanTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError(null);

    fetchAutoTaggingPlanTypes()
      .then((types) => {
        if (!mounted) return;
        setPlanTypes(types);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setPlanTypes([]);
        setError(err instanceof Error ? err.message : "Failed to load plan types");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const objectTypeOptions = useMemo(
    () =>
      planTypes.map((item) => ({
        code: item.code,
        name: item.name,
      })),
    [planTypes],
  );

  const defaultObjectType = useMemo(
    () => resolveDefaultObjectType(planTypes),
    [planTypes],
  );

  const getSubTypesForObjectType = useCallback(
    (objectType: string) => getSubTypesForObjectTypeFromList(planTypes, objectType),
    [planTypes],
  );

  return {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    getSubTypesForObjectType,
    loading,
    error,
  };
}
